/* eslint-disable jsx-a11y/anchor-is-valid */

import * as Yup from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'

import React, { useState } from 'react'

import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { register, confirmRegistration, resendConfirmationCode } from '../redux/AuthCRUD'
import { useFormik } from 'formik'
import { useAuth } from 'app/context/auth-context'
import { States } from 'app/helpers/MiscUtils'
import { Button, Modal } from 'react-bootstrap-v5'
import TnC from './TnC'

// Add to initialValues
const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  changepassword: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  acceptTerms: false,
  confirmationCode: '',
  recaptcha: '',
}

const registrationSchema = Yup.object().shape({
  firstname: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('First name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  lastname: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Last name is required'),
  city: Yup.string()
    .min(2, 'Minimum 2 characters')
    .max(50, 'Maximum 50 characters')
    .required('City is required'),
  state: Yup.string()
    .matches(/^[A-Z]{2}$/, 'Must be 2 letter state code (e.g. NY)')
    .required('State is required'),
  zip: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, 'Must be valid US ZIP code')
    .required('ZIP code is required'),
  phone: Yup.string()
    .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Must be format (XXX) XXX-XXXX')
    .required('Phone number is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters long')
    .matches(/[a-zA-Z]/, 'Password must contain at least one letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
    .required('Password is required'),
  changepassword: Yup.string()
    .required('Password confirmation is required')
    .when('password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
    }),
  acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
  confirmationCode: Yup.string().min(6, 'Confirmation code must be 6 digits').max(6, 'Confirmation code must be 6 digits'),
  recaptcha: Yup.string().required('Please verify you are not a robot')
})

export function Registration(props: { roles: string, existingEmail?: string, afterLoginHandler?: () => void, orgId?: string }) {
  const [loading, setLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false); // Track if user has registered
  const [isConfirmed, setIsConfirmed] = useState(false); // Track if user confirmation is complete
  const [resendTimer, setResendTimer] = useState<number>(0); // Timer for resend button
  const { login } = useAuth();
  const [showTermsModal, setShowTermsModal] = useState(false); // Add state for terms modal
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

// get sponsorId from query params if available
  const sponsorId = new URLSearchParams(window.location.search).get('sponsorId') || undefined;
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(prev => !prev);
  };

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }

  const formik = useFormik({
    initialValues: { ...initialValues, email: props.existingEmail || '' },
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      if (!isRegistered) {
        // First, handle the registration
        try {
          await register(values.email, values.firstname, values.lastname, values.phone, values.password, props.roles, props.orgId, sponsorId);
          setIsRegistered(true);
          setLoading(false);
        } catch (error) {
          setLoading(false);
          setSubmitting(false);
          setStatus('Registration process has broken');
        }
      } else {
        // Then, handle the confirmation of the registration
        try {
          await confirmRegistration(values.email, values.confirmationCode);
          setLoading(false);
          setIsConfirmed(true); // Mark confirmation as complete
          setStatus('');
          await login(values.email, values.password);
          if (props.afterLoginHandler) {
            props.afterLoginHandler();
          }
        } catch (error) {
          setLoading(false);
          setSubmitting(false);
          setIsConfirmed(false);
          setStatus('Confirmation code is incorrect');
        }
      }
    },
  })

  const handleRecaptchaChange = (value: string | null) => {
    formik.setFieldValue('recaptcha', value)
  }

  const handleResendConfirmationCode = async () => {
    try {
      if (!formik.values.email) {
        formik.setStatus('Please provide a valid email to resend the confirmation code.');
        return;
      }
      await resendConfirmationCode(formik.values.email); // Call the resend function
      formik.setStatus('Confirmation code resent successfully. Please check your email.');
      setResendTimer(60); // Start the timer
    } catch (error) {
      console.error('Error resending confirmation code:', error);
      formik.setStatus('Failed to resend confirmation code. Please try again later.');
    }
  };

  // Timer effect for the resend button
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timerId = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timerId);
    }
  }, [resendTimer]);

  return (
    <div className='w-lg-1200px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_signup_form'
        onSubmit={formik.handleSubmit}
      >
        {/* begin::Heading */}
        <div className='mb-10 text-center'>
          {/* begin::Title */}
          <h1 className='text-dark mb-3'> {props.roles.includes('Applicant') ? 'Create an Account' : 'Create an Employer Account'}</h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className='text-gray-400 fw-bold fs-4'>
            Already have an account?
            <Link to='/auth/login' style={{ marginLeft: '5px' }}>
              <span className='fw-bold text-primary'>Sign in</span>
            </Link>
          </div>
          {/* end::Link */}
        </div>
        {/* end::Heading */}

        {/* begin::Action */}

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {!isRegistered ? (
          <div className='d-flex flex-column flex-lg-row'>
            {/* Left Column - Personal & Contact */}
            <div className='flex-lg-row-fluid mb-10 mb-lg-0 me-lg-7 me-xl-10'>
              <div className='card card-flush bg-gray-100'>
                <div className='card-header'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold text-dark'>Personal Information</span>
                  </h3>
                </div>
                <div className='card-body pt-5'>
                  <div className='row g-5 g-xl-8'>
                    <div className='col-md-6'>
                      <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>First Name</label>
                        <input
                          type='text'

                          {...formik.getFieldProps('firstname')}
                          className={clsx(
                            'form-control form-control-lg form-control-solid',

                            'py-3 px-4',
                            'rounded-2',
                            'fs-6',
                            { 'is-invalid': formik.touched.firstname && formik.errors.firstname },
                            { 'is-valid': formik.touched.firstname && !formik.errors.firstname }
                          )}
                        />
                        {formik.touched.firstname && formik.errors.firstname && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.firstname}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>Last Name</label>
                        <input
                          type='text'

                          {...formik.getFieldProps('lastname')}
                          className={clsx(
                            'form-control form-control-lg form-control-solid',

                            'py-3 px-4',
                            'rounded-2',
                            'fs-6',
                            { 'is-invalid': formik.touched.lastname && formik.errors.lastname },
                            { 'is-valid': formik.touched.lastname && !formik.errors.lastname }
                          )}
                        />
                        {formik.touched.lastname && formik.errors.lastname && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.lastname}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row g-5 g-xl-8'>
                    <div className='col-md-6'>
                      <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>Email</label>
                        <input
                          type='email'
                          {...formik.getFieldProps('email')}
                          className={clsx(
                            'form-control form-control-lg form-control-solid',

                            'py-3 px-4',
                            'rounded-2',
                            'fs-6',
                            { 'is-invalid': formik.touched.email && formik.errors.email },
                            { 'is-valid': formik.touched.email && !formik.errors.email }
                          )}
                          readOnly={!!props.existingEmail}
                        />
                        {formik.touched.email && formik.errors.email && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.email}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>Mobile phone</label>
                        <input
                          type='tel'
                          placeholder='(XXX) XXX-XXXX'
                          value={formik.values.phone}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            formik.setFieldValue('phone', formatted);
                          }}
                          className={clsx(
                            'form-control form-control-lg form-control-solid',
                            'py-3 px-4',
                            'rounded-2',
                            'fs-6',
                            { 'is-invalid': formik.touched.phone && formik.errors.phone },
                            { 'is-valid': formik.touched.phone && !formik.errors.phone }
                          )}
                        />
                        {formik.touched.phone && formik.errors.phone && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.phone}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='separator separator-dashed my-10'></div>

                  <div className='row g-5 g-xl-8'>
                    <div className='col-md-12'>
                      <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>City</label>
                        <input
                          type='text'

                          {...formik.getFieldProps('city')}
                          className={clsx(
                            'form-control form-control-lg form-control-solid',

                            'py-3 px-4',
                            'rounded-2',
                            'fs-6',
                            { 'is-invalid': formik.touched.city && formik.errors.city },
                            { 'is-valid': formik.touched.city && !formik.errors.city }
                          )}
                        />
                        {formik.touched.city && formik.errors.city && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.city}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row g-5 g-xl-8'>
                    <div className='col-md-6'>
                      <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>State</label>
                        <select
                          {...formik.getFieldProps('state')}
                          className={clsx(
                            'form-select form-select-lg form-control-solid',
                            'py-3 px-4',
                            'rounded-2',
                            'fs-6',
                            { 'is-invalid': formik.touched.state && formik.errors.state },
                            { 'is-valid': formik.touched.state && !formik.errors.state }
                          )}
                        >
                          <option value=''>Select State</option>
                          {States.map((state) => (
                            <option key={state.code} value={state.code}>
                              {state.name} ({state.code})
                            </option>
                          ))}
                        </select>
                        {formik.touched.state && formik.errors.state && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.state}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>ZIP Code</label>
                        <input
                          type='text'

                          {...formik.getFieldProps('zip')}
                          className={clsx(
                            'form-control form-control-lg form-control-solid',

                            'py-3 px-4',
                            'rounded-2',
                            'fs-6',
                            { 'is-invalid': formik.touched.zip && formik.errors.zip },
                            { 'is-valid': formik.touched.zip && !formik.errors.zip }
                          )}
                        />
                        {formik.touched.zip && formik.errors.zip && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.zip}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Security & Verification */}
            <div className='flex-lg-auto min-w-lg-300px'>
              <div className='card card-flush'>
                <div className='card-header'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold text-dark'>Login Credentials</span>
                  </h3>
                </div>
                <div className='card-body pt-5'>
                  <div className='fv-row mb-10'>
                    <label className='required fw-bold fs-6 mb-2'>Password</label>
                    <div className='input-group'>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        {...formik.getFieldProps('password')}
                        className={clsx(
                          'form-control form-control-lg form-control-solid',
                          'py-3 px-4',
                          'rounded-2',
                          'fs-6',
                          { 'is-invalid': formik.touched.password && formik.errors.password },
                          { 'is-valid': formik.touched.password && !formik.errors.password }
                        )}
                      />
                      <button
                        title={showPassword ? 'hide password' : 'show password'}
                        type='button'
                        className='btn btn-outline-secondary'
                        onClick={togglePasswordVisibility}
                      >
                        <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                      </button>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.password}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className='fv-row mb-10'>
                    <label className='required fw-bold fs-6 mb-2'>Confirm Password</label>
                    <div className='input-group'>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        {...formik.getFieldProps('changepassword')}
                        className={clsx(
                          'form-control form-control-lg form-control-solid',
                          'py-3 px-4',
                          'rounded-2',
                          'fs-6',
                          { 'is-invalid': formik.touched.changepassword && formik.errors.changepassword },
                          { 'is-valid': formik.touched.changepassword && !formik.errors.changepassword }
                        )}
                      />
                      <button
                        title={showPassword ? 'hide password' : 'show password'}
                        type='button'
                        className='btn btn-outline-secondary'
                        onClick={toggleConfirmPasswordVisibility}
                      >
                        <i className={showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                      </button>
                    </div>
                    {formik.touched.changepassword && formik.errors.changepassword && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.changepassword}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className='separator separator-dashed my-10'></div>

                  <div className='fv-row mb-10'>
                    <ReCAPTCHA
                      sitekey="6LdLN3QqAAAAAP7YgsvqK5NYdkkVogZVbknkAnI2"
                      onChange={handleRecaptchaChange}
                    />
                    {formik.touched.recaptcha && formik.errors.recaptcha && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.recaptcha}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className='fv-row mb-10'>
                    <div className='form-check form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='kt_login_toc_agree'
                        {...formik.getFieldProps('acceptTerms')}
                      />
                      <label
                        className='form-check-label fw-semibold text-gray-600'
                        htmlFor='kt_login_toc_agree'
                      >
                        I Agree to {' '}
                        <a
                          href='#'
                          className='link-primary'
                          style={{ textDecoration: 'underline' }}
                          onClick={(e) => {
                            e.preventDefault();
                            setShowTermsModal(true);
                          }}
                        >
                          Terms and Conditions
                        </a>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : !isConfirmed ? (
          // Confirmation Code Form
          <>
            <div className='fv-row mb-7'>
              <div className='text-center mb-10'>
                <h3 className='text-primary mb-3'>Confirmation Code</h3>
                <p>Please check your email for the confirmation code.</p>
                <p>If you didn't receive the code, please check your spam folder or click the button below to resend it.</p>
                <button
                  type='button'
                  className='btn btn-link'
                  onClick={handleResendConfirmationCode}
                  disabled={resendTimer > 0} // Disable button if timer is active
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Confirmation Code'}
                </button>
                <p className='text-muted'>Enter the confirmation code sent to your email:</p>
              </div>
            </div>
            <div className='fv-row mb-7'>
              <input
                type='text'

                autoComplete='off'
                {...formik.getFieldProps('confirmationCode')}
                className={clsx('form-control form-control-lg form-control-solid', {
                  'is-invalid': formik.touched.confirmationCode && formik.errors.confirmationCode,
                  'is-valid': formik.touched.confirmationCode && !formik.errors.confirmationCode,
                })}
              />
              {formik.touched.confirmationCode && formik.errors.confirmationCode && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.confirmationCode}</span>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          // Success Message after Confirmation
          <div className='text-center mb-10'>
            <h3 className='text-success mb-3'>Confirmation Completed!</h3>
            <p>Your account has been successfully registered and confirmed.</p>

            <Link to='/auth/login' className='link-primary fw-bolder'>
              Go to Sign In page
            </Link>
          </div>
        )}
        {/* Submit Button */}
        {!isConfirmed && (
          <div className='d-flex justify-content-center'>
            <button type='submit' id='kt_sign_up_submit' className='btn btn-lg btn-primary mb-5' disabled={formik.isSubmitting || !formik.isValid || (!isRegistered && !formik.values.acceptTerms)}>
              {!loading && (
                <span className='indicator-label'>
                  <i className='bi bi-arrow-right-circle-fill fs-2 me-2'></i>
                  {!isRegistered ? 'Register' : 'Confirm'}
                </span>
              )}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        )}
      </form>
      <Modal show={showTermsModal} onHide={() => setShowTermsModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Terms and Conditions</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <TnC></TnC>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowTermsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}